import { useState, useRef, useCallback } from "react";

const SpeechRecognitionCtor = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;

export function useSpeechRecognition() {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  const [audioLevel, setAudioLevel] = useState(0);

  const recognitionRef = useRef(null);
  const startTimeRef = useRef(null);
  const audioCtxRef = useRef(null);
  const rafRef = useRef(null);
  const streamRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const onAutoStopRef = useRef(null);
  const finalTextRef = useRef("");

  const stopWaveform = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    if (audioCtxRef.current) audioCtxRef.current.close().catch(() => {});
    setAudioLevel(0);
  };

  const startWaveform = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteFrequencyData(data);
        const avg = data.reduce((a, b) => a + b, 0) / data.length;
        setAudioLevel(Math.min(1, avg / 80));
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (e) {
      // mic permission denied for waveform visuals only — recognition still attempts to run
    }
  };

  const start = useCallback((onAutoStop) => {
    if (!SpeechRecognitionCtor) return;
    onAutoStopRef.current = onAutoStop;
    const recognition = new SpeechRecognitionCtor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    finalTextRef.current = "";
    recognition.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const chunk = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalTextRef.current += `${chunk} `;
        else interim += chunk;
      }
      setTranscript(finalTextRef.current.trim());
      setInterimTranscript(interim);

      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = setTimeout(() => recognition.stop(), 2500);
    };
    recognition.onerror = () => {};
    recognition.onend = () => {
      setIsListening(false);
      stopWaveform();
      clearTimeout(silenceTimerRef.current);
      if (onAutoStopRef.current) onAutoStopRef.current(finalTextRef.current.trim());
    };

    recognitionRef.current = recognition;
    startTimeRef.current = Date.now();
    setTranscript("");
    setInterimTranscript("");
    setIsListening(true);
    startWaveform();
    recognition.start();
  }, []);

  const stop = useCallback(() => {
    if (recognitionRef.current) recognitionRef.current.stop();
  }, []);

  const getDurationSeconds = useCallback(() => (startTimeRef.current ? (Date.now() - startTimeRef.current) / 1000 : 0), []);

  return {
    supported: !!SpeechRecognitionCtor,
    isListening, transcript, interimTranscript, audioLevel,
    start, stop, getDurationSeconds,
  };
}
